import { cityProvinceFallback, provinceByAdminCode, provinceById, provinces } from "../data/provinces";
import type { ProvinceStat, Scope, University } from "../types";

export function getProvinceId(university: University): string | null {
  if (/^\d{4}/.test(university.code)) {
    const adminCode = university.code.slice(2, 4);
    return provinceByAdminCode.get(adminCode)?.id ?? null;
  }

  return cityProvinceFallback[university.location] ?? null;
}

export function matchesScope(university: University, scope: Scope): boolean {
  switch (scope) {
    case "ordinary":
      return university.remark !== "军校";
    case "undergraduate":
      return university.level === "本科";
    case "vocational":
      return university.level === "专科";
    case "private":
      return university.remark.includes("民办");
    case "military":
      return university.remark === "军校";
    default:
      return true;
  }
}

export function filterUniversities(universities: University[], scope: Scope): University[] {
  return universities.filter((university) => matchesScope(university, scope));
}

export function getProvinceStats(universities: University[], scope: Scope): ProvinceStat[] {
  const scoped = filterUniversities(universities, scope);
  const byProvince = new Map<string, University[]>();

  for (const university of scoped) {
    const provinceId = getProvinceId(university);
    if (!provinceId) continue;
    byProvince.set(provinceId, [...(byProvince.get(provinceId) ?? []), university]);
  }

  return provinces.map((province) => {
    const rows = byProvince.get(province.id) ?? [];
    return {
      ...province,
      count: rows.length,
      undergraduate: rows.filter((row) => row.level === "本科").length,
      vocational: rows.filter((row) => row.level === "专科").length,
      privateCount: rows.filter((row) => row.remark.includes("民办")).length,
      military: rows.filter((row) => row.remark === "军校").length
    };
  });
}

export function getUniversitiesByProvince(
  universities: University[],
  provinceId: string | null,
  scope: Scope
): University[] {
  return filterUniversities(universities, scope).filter((university) => {
    if (!provinceId) return true;
    return getProvinceId(university) === provinceId;
  });
}

export function getTopProvinceStats(stats: ProvinceStat[], limit = 12): ProvinceStat[] {
  return [...stats]
    .filter((stat) => stat.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function getSelectedProvinceName(provinceId: string | null): string {
  if (!provinceId) return "全国";
  return provinceById.get(provinceId)?.name ?? provinceId;
}

export function getLevelDistribution(universities: University[], scope: Scope) {
  const scoped = filterUniversities(universities, scope);
  return [
    { name: "本科", value: scoped.filter((item) => item.level === "本科").length, color: "oklch(0.58 0.12 245)" },
    { name: "专科", value: scoped.filter((item) => item.level === "专科").length, color: "oklch(0.62 0.13 150)" },
    { name: "未标注", value: scoped.filter((item) => !item.level).length, color: "oklch(0.72 0.14 82)" }
  ].filter((item) => item.value > 0);
}

export function getRemarkDistribution(universities: University[], scope: Scope) {
  const scoped = filterUniversities(universities, scope);
  const buckets = new Map<string, number>();

  for (const university of scoped) {
    const label = university.remark || "普通";
    buckets.set(label, (buckets.get(label) ?? 0) + 1);
  }

  return Array.from(buckets.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

export function searchUniversities(rows: University[], query: string): University[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return rows;

  return rows.filter((row) =>
    [row.name, row.code, row.department, row.location, row.level, row.remark]
      .join(" ")
      .toLowerCase()
      .includes(normalized)
  );
}
